import { useEffect, useState } from "react";
import { Plus, Loader2, Trash2, Pencil, PiggyBank, ChevronDown, ChevronRight, Printer } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { openProtectedFile } from "@/components/employee/tabHelpers";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export default function CashRegistersPage() {
  const [items, setItems] = useState([]);
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [r, s] = await Promise.all([
        api.get("/cash-registers"),
        api.get("/sites"),
      ]);
      setItems(r.data || []);
      setSites(s.data || []);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Betöltés sikertelen.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const onDelete = async (r) => {
    if (!window.confirm(`Törlöd: "${r.name}"?`)) return;
    try {
      await api.delete(`/cash-registers/${r._id}`);
      toast.success("Törölve.");
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Törlés sikertelen.");
    }
  };

  return (
    <div className="space-y-6 px-4 py-6 sm:px-6 lg:px-8" data-testid="cash-registers-page">
      <header className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <div className="flex items-center gap-1.5 text-muted-foreground/80">
            <PiggyBank className="h-3.5 w-3.5" strokeWidth={2} />
            <span className="text-[10px] font-semibold uppercase tracking-[0.22em]">
              Pénzügy
            </span>
          </div>
          <h1 className="mt-1.5 text-2xl font-semibold tracking-tight">Házipénztárak</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Telephely-szintű készpénz-pénztárak. EFO kifizetésekhez használhatók.
          </p>
        </div>
        <Button
          onClick={() => { setEditing(null); setOpen(true); }}
          data-testid="new-cash-register"
        >
          <Plus className="h-4 w-4" /> Új házipénztár
        </Button>
      </header>

      <section className="overflow-hidden rounded-xl border border-border bg-[hsl(var(--sipx-elevated))]">
        {loading ? (
          <div className="flex items-center justify-center px-6 py-14 text-sm text-muted-foreground">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Betöltés…
          </div>
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center gap-3 px-6 py-14 text-center">
            <div className="grid h-10 w-10 place-items-center rounded-full border border-border bg-background">
              <PiggyBank className="h-4 w-4 text-muted-foreground" />
            </div>
            <p className="text-sm font-medium">Nincs házipénztár</p>
            <p className="text-xs text-muted-foreground">
              Vegyél fel egy házipénztárat egy telephelyhez.
            </p>
          </div>
        ) : (
          <ul className="divide-y divide-border">
            {items.map((r) => (
              <CashRegisterRow
                key={r._id}
                register={r}
                onEdit={() => { setEditing(r); setOpen(true); }}
                onDelete={() => onDelete(r)}
              />
            ))}
          </ul>
        )}
      </section>

      <CashRegisterFormDialog
        open={open}
        onOpenChange={setOpen}
        initial={editing}
        sites={sites}
        onSaved={() => { setOpen(false); setEditing(null); load(); }}
      />
    </div>
  );
}

function CashRegisterFormDialog({ open, onOpenChange, initial, sites, onSaved }) {
  const isEdit = !!initial?._id;
  const [name, setName] = useState("");
  const [siteId, setSiteId] = useState("");
  const [notes, setNotes] = useState("");
  const [active, setActive] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setName(initial?.name || "");
    setSiteId(initial?.site_id || "");
    setNotes(initial?.notes || "");
    setActive(initial?.active !== false);
  }, [open, initial]);

  const onSubmit = async () => {
    if (!name.trim()) { toast.error("A név kötelező."); return; }
    if (!siteId) { toast.error("Válassz telephelyet."); return; }
    setSaving(true);
    try {
      const body = { name: name.trim(), site_id: siteId, notes: notes.trim() || null, active };
      if (isEdit) await api.patch(`/cash-registers/${initial._id}`, body);
      else await api.post("/cash-registers", body);
      toast.success(isEdit ? "Frissítve." : "Létrehozva.");
      onSaved?.();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Mentés sikertelen.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="cash-register-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Házipénztár szerkesztése" : "Új házipénztár"}</DialogTitle>
          <DialogDescription>
            Egy telephelyhez több házipénztár is tartozhat (pl. &bdquo;Pult&rdquo;, &bdquo;Iroda&rdquo;).
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div>
            <Label htmlFor="cr-name">Név *</Label>
            <Input
              id="cr-name" value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={'pl. "Pult"'}
              data-testid="cr-name"
            />
          </div>
          <div>
            <Label htmlFor="cr-site">Telephely *</Label>
            <Select value={siteId} onValueChange={setSiteId}>
              <SelectTrigger id="cr-site" data-testid="cr-site">
                <SelectValue placeholder="Válassz telephelyet…" />
              </SelectTrigger>
              <SelectContent>
                {sites.map((s) => (
                  <SelectItem key={s._id} value={s._id}>{s.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="cr-notes">Megjegyzés</Label>
            <Textarea
              id="cr-notes" rows={2} value={notes}
              onChange={(e) => setNotes(e.target.value)}
              data-testid="cr-notes"
            />
          </div>
          <label className="flex items-center justify-between rounded-lg border border-border bg-muted/10 px-4 py-3">
            <span className="text-sm font-medium">Aktív</span>
            <Switch checked={active} onCheckedChange={setActive} data-testid="cr-active" />
          </label>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={saving}>
            Mégse
          </Button>
          <Button onClick={onSubmit} disabled={saving} data-testid="cr-save">
            {saving && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
            {isEdit ? "Mentés" : "Létrehozás"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


const huFmt = new Intl.NumberFormat("hu-HU", { maximumFractionDigits: 0 });
const formatFt = (n) => (n == null ? "—" : `${huFmt.format(Number(n))} Ft`);
const formatDate = (s) => {
  if (!s) return "—";
  try {
    return new Date(s).toLocaleString("hu-HU", {
      year: "numeric", month: "2-digit", day: "2-digit",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return s; }
};

function CashRegisterRow({ register, onEdit, onDelete }) {
  const [open, setOpen] = useState(false);
  const [tx, setTx] = useState(null);
  const [loading, setLoading] = useState(false);

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next && tx === null) {
      setLoading(true);
      try {
        const { data } = await api.get(`/cash-registers/${register._id}/transactions`);
        setTx(data);
      } catch {
        setTx({ items: [], total_out: 0, count: 0 });
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <li className="px-0 py-0" data-testid={`cr-row-${register._id}`}>
      <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-4">
        <button
          type="button"
          onClick={toggle}
          className="flex flex-1 items-center gap-3 text-left"
          data-testid={`cr-toggle-${register._id}`}
        >
          {open ? (
            <ChevronDown className="h-4 w-4 text-muted-foreground" />
          ) : (
            <ChevronRight className="h-4 w-4 text-muted-foreground" />
          )}
          <div className="min-w-0">
            <p className="flex items-center gap-2 text-sm font-medium">
              {register.name}
              {register.active === false && (
                <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-muted-foreground">
                  inaktív
                </span>
              )}
            </p>
            <p className="text-[11px] text-muted-foreground">
              {register.site_name}
              {register.company_name && <> · {register.company_name}</>}
            </p>
            {register.notes && (
              <p className="mt-1 max-w-2xl text-xs text-muted-foreground">
                {register.notes}
              </p>
            )}
          </div>
        </button>
        <div className="flex items-center gap-1">
          <Button
            variant="ghost" size="sm"
            onClick={onEdit}
            data-testid={`cr-edit-${register._id}`}
          >
            <Pencil className="h-3.5 w-3.5" /> Szerkesztés
          </Button>
          <Button
            variant="ghost" size="icon"
            onClick={onDelete}
            className="h-8 w-8 text-rose-600 hover:bg-rose-50 hover:text-rose-700 dark:hover:bg-rose-500/10"
            data-testid={`cr-delete-${register._id}`}
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>
      {open && (
        <div
          className="border-t border-border bg-muted/[0.04] px-5 pb-4 pt-3"
          data-testid={`cr-tx-${register._id}`}
        >
          <div className="mb-2 flex items-center justify-between">
            <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
              Tranzakciók
            </p>
            {tx && (
              <p className="text-xs text-muted-foreground">
                {tx.count} tétel · összes kifizetés:{" "}
                <b className="text-rose-600 dark:text-rose-400 tabular-nums">
                  −{formatFt(tx.total_out)}
                </b>
              </p>
            )}
          </div>
          {loading ? (
            <div className="flex items-center justify-center py-4 text-xs text-muted-foreground">
              <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" /> Betöltés…
            </div>
          ) : !tx || tx.items.length === 0 ? (
            <p className="rounded-md border border-dashed border-border bg-background px-3 py-4 text-center text-xs text-muted-foreground">
              Még nincs tranzakció ezen a házipénztáron.
            </p>
          ) : (
            <div className="overflow-x-auto rounded-md border border-border bg-background">
              <table className="w-full min-w-[640px] text-xs">
                <thead>
                  <tr className="border-b border-border bg-muted/40 text-muted-foreground">
                    <th className="px-3 py-2 text-left font-medium uppercase tracking-wider">Időpont</th>
                    <th className="px-3 py-2 text-left font-medium uppercase tracking-wider">Munkavállaló</th>
                    <th className="px-3 py-2 text-left font-medium uppercase tracking-wider">Kategória</th>
                    <th className="px-3 py-2 text-left font-medium uppercase tracking-wider">Tárgyhó</th>
                    <th className="px-3 py-2 text-right font-medium uppercase tracking-wider">Összeg</th>
                    <th className="px-3 py-2 text-right font-medium uppercase tracking-wider">Bizonylat</th>
                  </tr>
                </thead>
                <tbody>
                  {tx.items.map((t) => (
                    <tr
                      key={t._id}
                      className="border-b border-border/60 last:border-0"
                      data-testid={`tx-row-${t._id}`}
                    >
                      <td className="whitespace-nowrap px-3 py-2 tabular-nums text-muted-foreground">
                        {formatDate(t.paid_at)}
                      </td>
                      <td className="whitespace-nowrap px-3 py-2 font-medium">
                        {t.employee_name}
                      </td>
                      <td className="whitespace-nowrap px-3 py-2 text-muted-foreground">
                        {t.category_label}
                      </td>
                      <td className="whitespace-nowrap px-3 py-2 text-muted-foreground">
                        {t.pay_month || "—"}
                      </td>
                      <td className="whitespace-nowrap px-3 py-2 text-right font-semibold tabular-nums text-rose-600 dark:text-rose-400">
                        −{formatFt(t.amount)}
                      </td>
                      <td className="whitespace-nowrap px-3 py-2 text-right">
                        <button
                          type="button"
                          onClick={() =>
                            openProtectedFile(
                              `/salaries/${t._id}/payment-receipt.pdf`,
                              "application/pdf",
                              `ber-kifizetes-${t._id}.pdf`
                            )
                          }
                          className="inline-flex items-center gap-1 rounded-md border border-border bg-background px-2 py-1 text-[11px] text-muted-foreground hover:text-foreground"
                          data-testid={`tx-receipt-${t._id}`}
                        >
                          <Printer className="h-3 w-3" /> PDF
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </li>
  );
}
